<template>
  <p
    v-if="!rows.length"
    class="px-4 py-6 text-center text-sm text-gray-500"
    data-cy="map-empty"
  >
    No columns.
  </p>

  <div v-else-if="mobile" class="space-y-3" data-cy="map-cards">
    <div class="flex items-center justify-between gap-2 text-sm">
      <span class="text-gray-500" data-cy="map-sample-label">{{
        sampleText
      }}</span>
      <MapNextSample :disabled="sampleDisabled" @click="emit('next-sample')" />
    </div>
    <div
      v-for="row in rows"
      :key="row.index"
      class="space-y-2 rounded-lg border border-gray-200 bg-white p-4"
      :data-cy="`map-row-${row.index}`"
    >
      <div class="flex items-start justify-between gap-2">
        <span class="min-w-0 break-words font-medium text-gray-900">{{
          row.header || "(no header)"
        }}</span>
        <MapStatusPill
          :state="row.state"
          :data-cy="`map-status-${row.index}`"
        />
      </div>
      <div class="text-sm text-gray-600" :data-cy="`map-sample-${row.index}`">
        <span class="text-gray-500">Sample: </span
        >{{ displayValue(row.sample) }}
      </div>
      <MapFieldPicker
        :row="row"
        :options="optionsFor(row)"
        :disabled="disabled"
        @pick="onPick"
      />
      <div class="text-sm" :data-cy="`map-imported-${row.index}`">
        <span class="text-gray-500">Imported as:</span>
        <MapImportedAs :row="row" />
      </div>
    </div>
  </div>

  <div
    v-else
    class="overflow-x-auto rounded-lg border border-gray-200 bg-white"
  >
    <table class="w-full text-left text-sm" data-cy="map-table">
      <thead class="bg-gray-50 text-xs uppercase text-gray-700">
        <tr>
          <th class="px-4 py-3 font-semibold">Column in file</th>
          <th class="px-4 py-3 font-semibold">
            <div class="flex items-center gap-2">
              <span>Sample</span>
              <MapNextSample
                :disabled="sampleDisabled"
                @click="emit('next-sample')"
              />
              <span
                class="font-normal normal-case text-gray-500"
                data-cy="map-sample-label"
                >{{ sampleText }}</span
              >
            </div>
          </th>
          <th class="w-72 px-4 py-3 font-semibold">Sera field</th>
          <th class="px-4 py-3 font-semibold">Imported as</th>
          <th class="px-4 py-3 font-semibold">Status</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.index"
          class="border-t border-gray-200"
          :data-cy="`map-row-${row.index}`"
        >
          <td class="px-4 py-3 font-medium text-gray-900">
            {{ row.header || "(no header)" }}
          </td>
          <td
            class="px-4 py-3 text-gray-500"
            :data-cy="`map-sample-${row.index}`"
          >
            {{ displayValue(row.sample) }}
          </td>
          <td class="px-4 py-2">
            <MapFieldPicker
              :row="row"
              :options="optionsFor(row)"
              :disabled="disabled"
              @pick="onPick"
            />
          </td>
          <td class="px-4 py-3" :data-cy="`map-imported-${row.index}`">
            <MapImportedAs :row="row" />
          </td>
          <td class="px-4 py-3">
            <MapStatusPill
              :state="row.state"
              :data-cy="`map-status-${row.index}`"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { displayValue, type MapOption, type MapRow } from "./mapColumns";
import MapFieldPicker from "./MapFieldPicker.vue";
import MapImportedAs from "./MapImportedAs.vue";
import MapNextSample from "./MapNextSample.vue";
import MapStatusPill from "./MapStatusPill.vue";

defineProps<{
  rows: MapRow[];
  optionsFor: (row: MapRow) => MapOption[];
  disabled: boolean;
  mobile: boolean;
  sampleText: string;
  sampleDisabled: boolean;
}>();

const emit = defineEmits<{
  pick: [row: MapRow, value: unknown];
  "next-sample": [];
}>();

function onPick(row: MapRow, value: unknown) {
  emit("pick", row, value);
}
</script>
